import { motion, AnimatePresence } from "framer-motion";

/**
 * G5.b — visceral live-session signals layered on top of any photo card.
 *
 *   <PhotoLiveOverlay
 *      photoId         = string
 *      participants    = [{participant_id, display_name, role, current_photo_id}]
 *      meId            = your own participant_id (so we hide your own avatar
 *                        on the photo you're already viewing)
 *      burstKey        = number / string that changes whenever a fresh
 *                        heart_burst arrives for this photo (drives the
 *                        AnimatePresence re-mount)
 *      burstLabel      = "Best Friend" (used for the reduced-motion fallback)
 *   />
 *
 * Renders:
 *   • Bottom-right ring of avatar initials for other participants currently
 *     viewing this photo (max 3 visible + "+N" overflow).
 *   • A brief gold particle burst centered on the card when burstKey changes.
 *     Respects prefers-reduced-motion via Tailwind's `motion-safe:` /
 *     `motion-reduce:` modifiers — falls back to a static "💛 [name]" pill.
 */
export default function PhotoLiveOverlay({ photoId, participants, meId, burstKey, burstLabel }) {
  const viewers = (participants || []).filter(
    (p) => p.current_photo_id === photoId && p.participant_id !== meId,
  );
  const visible = viewers.slice(0, 3);
  const overflow = Math.max(0, viewers.length - 3);

  return (
    <>
      {/* Viewer avatars */}
      {viewers.length > 0 && (
        <div className="absolute bottom-2 right-2 z-10 inline-flex items-center -space-x-1.5" data-testid={`live-viewers-${photoId}`}>
          {visible.map((p) => (
            <span
              key={p.participant_id}
              title={`${p.display_name} is looking at this`}
              className={`inline-flex items-center justify-center w-5 h-5 text-[9px] font-medium rounded-full ring-2 ring-[#FAF9F6] ${p.role === "primary" ? "bg-[#C9A961] text-stone-900" : "bg-stone-700 text-[#F5EFE0]"}`}
              data-testid={`live-viewer-avatar-${p.participant_id}`}
            >
              {(p.display_name || "?").slice(0, 1).toUpperCase()}
            </span>
          ))}
          {overflow > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-[9px] font-medium rounded-full ring-2 ring-[#FAF9F6] bg-stone-900 text-[#F5EFE0]">
              +{overflow}
            </span>
          )}
        </div>
      )}

      {/* Heart burst — particle animation (motion-safe) + static badge (motion-reduce) */}
      <AnimatePresence>
        {burstKey != null && (
          <motion.div
            key={`burst-${burstKey}`}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: [0, 1, 1, 0], scale: [0.6, 1.3, 1.6, 1.8] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut", times: [0, 0.25, 0.6, 1] }}
            className="motion-safe:flex motion-reduce:hidden absolute inset-0 z-20 items-center justify-center pointer-events-none"
            data-testid={`heart-burst-${photoId}`}
            aria-hidden
          >
            <span className="text-5xl" style={{ color: "#C9A961", filter: "drop-shadow(0 4px 12px rgba(201,169,97,.55))" }}>♥</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {burstKey != null && (
          <motion.div
            key={`burst-pill-${burstKey}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="motion-safe:hidden motion-reduce:flex absolute top-2 left-2 z-20 px-2 py-0.5 bg-[#C9A961] text-stone-900 text-[10px] uppercase tracking-[0.18em] pointer-events-none"
            data-testid={`heart-burst-pill-${photoId}`}
          >
            ♥ {burstLabel || "loved"}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
